import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import React, { useMemo } from 'react';

import quickenLogoReveal from 'assets/logos/QuickenLogoReveal.json';
import { qknColors } from 'app-components/utils/theme';
import useStartupThemeMode from 'hooks/useStartupThemeMode';
import { hexToLottieRgb, recolorLottieWhiteToRgb } from 'utils/recolorLottieJson';

const LOADING_LOTTIE_SIZE_PX = 280;
const ANIMATION_START_DELAY_MS = 400;
const PAGE_FADE_MS = 400;

/*
 * No ThemeProvider / useTheme() this early in boot (PersistLoading renders before the store), so the mode is the
 * last saved appearance, else the system setting. Use qknColors for the same hex values as the light/dark palettes:
 * - background.paper → grey[0] / grey[950]
 * - logo on paper → primary.main (blue[600]) in light, common.white on dark (matches primary.contrastText usage)
 */
const LIGHT_BG = qknColors.grey[0];
const DARK_BG = qknColors.grey[950];
const LOADING_LOGO_HEX_LIGHT = qknColors.blue[600];
const LOADING_LOGO_HEX_DARK = qknColors.common.white;

const PersistLoading = (): React.ReactElement => {
  const lottieRef = React.useRef<LottieRefCurrentProps | null>(null);
  const [pageVisible, setPageVisible] = React.useState(false);
  const prefersDarkMode = useStartupThemeMode() === 'dark';

  const animationData = useMemo(() => {
    const hex = prefersDarkMode ? LOADING_LOGO_HEX_DARK : LOADING_LOGO_HEX_LIGHT;
    return recolorLottieWhiteToRgb(quickenLogoReveal as Record<string, unknown>, hexToLottieRgb(hex));
  }, [prefersDarkMode]);

  React.useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      setPageVisible(true);
    });
    return () => cancelAnimationFrame(frameId);
  }, []);

  React.useEffect(() => {
    const playId = window.setTimeout(() => {
      lottieRef.current?.play();
    }, ANIMATION_START_DELAY_MS);
    return () => window.clearTimeout(playId);
  }, [prefersDarkMode]);

  return (
    <Fade in={pageVisible} timeout={PAGE_FADE_MS}>
      <Box
        data-loading-surface='persist-rehydrate'
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: prefersDarkMode ? DARK_BG : LIGHT_BG,
        }}
      >
        <Box
          data-testid='loading-image'
          style={{
            width: LOADING_LOTTIE_SIZE_PX,
            // height: LOADING_LOTTIE_SIZE_PX,
          }}
          aria-busy
          aria-label='Loading'
        >
          <Lottie
            key={prefersDarkMode ? 'dark' : 'light'}
            lottieRef={lottieRef}
            animationData={animationData}
            loop
            autoplay={false}
            style={{ width: '100%', height: '100%' }}
          />
        </Box>
      </Box>
    </Fade>
  );
};

export default PersistLoading;
